import { Button, Checkbox, Loading, notify } from '@affine/component';
import { IntegrationService } from '@affine/core/modules/integration';
import type {
  GitHubBranch,
  GitHubContent,
  GitHubRepository,
} from '@affine/core/modules/integration/type';
import {
  FolderIcon,
  PageIcon,
  SearchIcon,
  ToggleDownIcon,
  ToggleRightIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './repo-browser.css';

interface TreeNode {
  content: GitHubContent;
  children?: TreeNode[];
  isExpanded?: boolean;
  isLoading?: boolean;
}

export interface GitHubFileSelection {
  owner: string;
  repo: string;
  branch: string;
  files: { path: string; name: string }[];
}

interface RepoBrowserProps {
  onImport: (selection: GitHubFileSelection) => void;
  onCancel: () => void;
}

export const RepoBrowser = ({ onImport, onCancel }: RepoBrowserProps) => {
  const github = useService(IntegrationService).github;
  const settings = useLiveData(github.settings$);

  const [repos, setRepos] = useState<GitHubRepository[]>([]);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(
    null
  );
  const [branches, setBranches] = useState<GitHubBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(true);

  // Filter repos based on search query
  const filteredRepos = useMemo(() => {
    if (!repoSearch.trim()) return repos;
    const searchLower = repoSearch.toLowerCase();
    return repos.filter(repo =>
      repo.full_name.toLowerCase().includes(searchLower)
    );
  }, [repos, repoSearch]);

  // Load repositories on mount
  useEffect(() => {
    if (!settings?.accessToken) return;

    const loadRepos = async () => {
      setLoadingRepos(true);
      try {
        const repoList = await github.listRepos(1, 100, 'pushed');
        setRepos(repoList);
      } catch (error) {
        notify.error({
          title: 'Failed to load repositories',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setLoadingRepos(false);
      }
    };

    loadRepos().catch(console.error);
  }, [github, settings?.accessToken]);

  // Load branches when repo is selected
  useEffect(() => {
    if (!selectedRepo) {
      setBranches([]);
      setSelectedBranch('');
      return;
    }

    const loadBranches = async () => {
      try {
        const branchList = await github.listBranches(
          selectedRepo.owner.login,
          selectedRepo.name,
          1,
          100
        );
        setBranches(branchList);
        // Auto-select default branch
        const defaultBranch = branchList.find(
          b => b.name === selectedRepo.default_branch
        );
        if (defaultBranch) {
          setSelectedBranch(defaultBranch.name);
        } else if (branchList.length > 0) {
          setSelectedBranch(branchList[0].name);
        }
      } catch (error) {
        notify.error({
          title: 'Failed to load branches',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    loadBranches().catch(console.error);
  }, [github, selectedRepo]);

  // Load contents when branch is selected
  useEffect(() => {
    if (!selectedRepo || !selectedBranch) {
      setTreeNodes([]);
      return;
    }

    const loadContents = async () => {
      setLoading(true);
      try {
        const contents = await github.getContents(
          selectedRepo.owner.login,
          selectedRepo.name,
          '',
          selectedBranch
        );

        const contentArray = Array.isArray(contents) ? contents : [contents];
        const nodes = contentArray
          .filter(c => c.type === 'dir' || c.name.endsWith('.md'))
          .sort((a, b) => {
            // Directories first, then files
            if (a.type === 'dir' && b.type !== 'dir') return -1;
            if (a.type !== 'dir' && b.type === 'dir') return 1;
            return a.name.localeCompare(b.name);
          })
          .map(c => ({ content: c }));

        setTreeNodes(nodes);
      } catch (error) {
        notify.error({
          title: 'Failed to load contents',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadContents().catch(console.error);
  }, [github, selectedRepo, selectedBranch]);

  const handleExpandFolder = useCallback(
    async (node: TreeNode, path: string[]) => {
      if (!selectedRepo || !selectedBranch) return;

      // If already has children, just toggle
      if (node.children) {
        setTreeNodes(prev =>
          updateNodeInTree(prev, path, { isExpanded: !node.isExpanded })
        );
        return;
      }

      // Load children
      setTreeNodes(prev => updateNodeInTree(prev, path, { isLoading: true }));

      try {
        const contents = await github.getContents(
          selectedRepo.owner.login,
          selectedRepo.name,
          node.content.path,
          selectedBranch
        );

        const contentArray = Array.isArray(contents) ? contents : [contents];
        const children = contentArray
          .filter(c => c.type === 'dir' || c.name.endsWith('.md'))
          .sort((a, b) => {
            if (a.type === 'dir' && b.type !== 'dir') return -1;
            if (a.type !== 'dir' && b.type === 'dir') return 1;
            return a.name.localeCompare(b.name);
          })
          .map(c => ({ content: c }));

        setTreeNodes(prev =>
          updateNodeInTree(prev, path, {
            children,
            isExpanded: true,
            isLoading: false,
          })
        );
      } catch (error) {
        notify.error({
          title: 'Failed to load folder contents',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        setTreeNodes(prev =>
          updateNodeInTree(prev, path, { isLoading: false })
        );
      }
    },
    [github, selectedRepo, selectedBranch]
  );

  const handleToggleFile = useCallback((path: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  const handleImport = useCallback(() => {
    if (!selectedRepo || !selectedBranch) return;

    const files = Array.from(selectedFiles).map(path => ({
      path,
      name: path.split('/').pop() || path,
    }));
    onImport({
      owner: selectedRepo.owner.login,
      repo: selectedRepo.name,
      branch: selectedBranch,
      files,
    });
  }, [selectedFiles, selectedRepo, selectedBranch, onImport]);

  const renderTreeNode = (node: TreeNode, path: string[], depth: number) => {
    const isFolder = node.content.type === 'dir';
    const isSelected = selectedFiles.has(node.content.path);

    return (
      <div key={node.content.path}>
        <div
          className={styles.treeItem}
          data-selected={isSelected}
          style={{ paddingLeft: 8 + depth * 16 }}
          onClick={() => {
            if (isFolder) {
              handleExpandFolder(node, path).catch(console.error);
            } else {
              handleToggleFile(node.content.path);
            }
          }}
        >
          <div className={styles.treeItemIndent}>
            {isFolder && (
              <span className={styles.treeItemIcon}>
                {node.isLoading ? (
                  <Loading size={14} />
                ) : node.isExpanded ? (
                  <ToggleDownIcon />
                ) : (
                  <ToggleRightIcon />
                )}
              </span>
            )}
          </div>
          <span className={styles.treeItemIcon}>
            {isFolder ? <FolderIcon /> : <PageIcon />}
          </span>
          <span className={styles.treeItemName}>{node.content.name}</span>
          {!isFolder && (
            <Checkbox
              className={styles.treeItemCheckbox}
              checked={isSelected}
              onChange={() => handleToggleFile(node.content.path)}
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
        {isFolder && node.isExpanded && node.children && (
          <div>
            {node.children.map((child, index) =>
              renderTreeNode(child, [...path, index.toString()], depth + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  if (!settings?.accessToken) {
    return (
      <div className={styles.emptyContainer}>
        <p>Please connect your GitHub account first.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.selectRow}>
          <span className={styles.selectLabel}>Repository</span>
          <div className={styles.selectWrapper}>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search repositories..."
                value={repoSearch}
                onChange={e => setRepoSearch(e.target.value)}
                disabled={loadingRepos}
              />
            </div>
            <select
              value={selectedRepo?.id || ''}
              onChange={e => {
                const repo = repos.find(r => r.id === Number(e.target.value));
                setSelectedRepo(repo || null);
                setSelectedFiles(new Set());
                // Clear search when repo is selected
                if (repo) {
                  setRepoSearch('');
                }
              }}
              disabled={loadingRepos}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4 }}
              size={Math.min(8, Math.max(2, filteredRepos.length + 1))}
            >
              <option value="">
                {loadingRepos
                  ? 'Loading...'
                  : filteredRepos.length === 0
                    ? repoSearch
                      ? 'No matching repositories'
                      : 'No repositories'
                    : 'Select a repository'}
              </option>
              {filteredRepos.map(repo => (
                <option key={repo.id} value={repo.id}>
                  {repo.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedRepo && (
          <div className={styles.selectRow}>
            <span className={styles.selectLabel}>Branch</span>
            <div className={styles.selectWrapper}>
              <select
                value={selectedBranch}
                onChange={e => {
                  setSelectedBranch(e.target.value);
                  setSelectedFiles(new Set());
                }}
                style={{ width: '100%', padding: '6px 8px', borderRadius: 4 }}
              >
                {branches.map(branch => (
                  <option key={branch.name} value={branch.name}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className={styles.fileTree}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Loading size={24} />
            <span style={{ marginLeft: 8 }}>Loading files...</span>
          </div>
        ) : treeNodes.length === 0 ? (
          <div className={styles.emptyContainer}>
            {selectedRepo ? (
              <p>No markdown files found in this repository.</p>
            ) : (
              <p>Select a repository to browse files.</p>
            )}
          </div>
        ) : (
          <div>
            {treeNodes.map((node, index) =>
              renderTreeNode(node, [index.toString()], 0)
            )}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <span className={styles.selectedCount}>
          {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''}{' '}
          selected
        </span>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={selectedFiles.size === 0}
          >
            Import
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper function to update a node in the tree
function updateNodeInTree(
  nodes: TreeNode[],
  path: string[],
  updates: Partial<TreeNode>
): TreeNode[] {
  if (path.length === 0) return nodes;

  const [currentIndex, ...restPath] = path;
  const index = parseInt(currentIndex, 10);

  return nodes.map((node, i) => {
    if (i !== index) return node;

    if (restPath.length === 0) {
      return { ...node, ...updates };
    }

    return {
      ...node,
      children: node.children
        ? updateNodeInTree(node.children, restPath, updates)
        : node.children,
    };
  });
}
